import { useCallback, useEffect, useState } from "react";
import type { BBJEvent, TGetId, THandleSelect } from "./types";

export const useListVirtualizedSelect = <T>(
  data: T[],
  getId: TGetId<T>,
  resetSelectedItemsEvent?: BBJEvent,
  selectedItemsProps?: T[],
  onSelectedItemsChange?: (selectedItems: T[]) => void
): {
  selectedItems: T[];
  handleSelect: THandleSelect<T>;
  handleSelectAll: () => void;
} => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const handleSelectAll = useCallback(() => {
    setSelectedItems((selectedItems) => {
      const nSelectedItems = selectedItems.length === data.length ? [] : [...data];
      if (onSelectedItemsChange) {
        onSelectedItemsChange(nSelectedItems);
      }
      return nSelectedItems;
    });
  }, [data, onSelectedItemsChange]);

  const handleSelect = useCallback(
    (item: T, index: number) => {
      const id = getId(item, index);
      setSelectedItems((selectedItems) => {
        const index = selectedItems.findIndex((v, index) => getId(v, index) === id);
        let nSelectedItems: T[];
        if (index === -1) {
          nSelectedItems = [...selectedItems, item];
        } else {
          nSelectedItems = [...selectedItems];
          nSelectedItems.splice(index, 1);
        }
        if (onSelectedItemsChange) {
          onSelectedItemsChange(nSelectedItems);
        }
        return nSelectedItems;
      });
    },
    [getId, onSelectedItemsChange]
  );

  useEffect(() => {
    if (resetSelectedItemsEvent) {
      const callbackRef = resetSelectedItemsEvent.register((id) => {
        if (id === undefined) {
          setSelectedItems([]);
        } else {
          setSelectedItems((selectedItems) => {
            const index = selectedItems.findIndex((v, index) => getId(v, index) === id);
            const nSelectedItems = [...selectedItems];
            nSelectedItems.splice(index, 1);
            return nSelectedItems;
          });
        }
      });
      return () => resetSelectedItemsEvent.unregister(callbackRef);
    }
    return undefined;
  }, [resetSelectedItemsEvent, getId]);

  useEffect(() => {
    if (selectedItemsProps !== undefined) {
      setSelectedItems(selectedItemsProps);
    }
  }, [selectedItemsProps]);

  return { selectedItems, handleSelect, handleSelectAll };
};
